import { useCallback, useMemo } from "react"

import { useNavigate } from "react-router-dom"

import {
    Layer,
    useOrderingLayers,
    getClusterCountLayerConfig,
    CLUSTER_LAYER_SIZE,
    getClusterLayerConfig,
    CAMPAIGNS_LAYER_CLUSTER,
    CAMPAIGNS_LAYER,
    CAMPAIGNS_LAYER_CLUSTER_COUNT,
    CAMPAIGNS_LAYER_HOVERED,
    MAX_ZOOM_LEVEL_CLUSTERED,
    CAMPAIGNS_SOURCE,
} from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"

import { CampaignModules } from "../../constants/modules"
import { useCampaignStateContext } from "../../contexts/CampaignContext"
import { getCampaignItemsLayerStyle } from "../../utils/getCampaignItemsLayerStyle"

import { MultiCampaignsSource } from "../MultiCampaignsSource"

export const ICON_SIZE = 60
export const COLOR_SIZE = 20

export function MultiCampaignsLayer() {

    const { hoveredCampaign } = useCampaignStateContext()
    useOrderingLayers([CAMPAIGNS_LAYER, CAMPAIGNS_LAYER_CLUSTER, CAMPAIGNS_LAYER_CLUSTER_COUNT, CAMPAIGNS_LAYER_HOVERED])
    const navigate = useNavigate()
    const { selectedNetwork } = useNetworksStateContext()
    const theme = useTheme()

    const handleCampaignLayerClick = useCallback((event) => {
        const feature = event.feature
        if (feature?.properties?.disabled) {
            return
        }
        const module = CampaignModules.find(module => module.module === feature.properties?.campaign_type)
        const road = JSON.parse(feature.properties?.linearLocation).road
        navigate(encodeURI(`/${selectedNetwork?.slug}/${module.path}/${road}/${feature.properties?.uuid}`))
    }, [selectedNetwork, navigate])

    const campaignClusterLayerConfig = useMemo(() => {
        if (!theme) {
            return null
        }
        return getClusterLayerConfig({
            paint: {
                "circle-color": theme.palette["common/gray_states"].dark,
                "circle-radius": CLUSTER_LAYER_SIZE,
            },
        })
    }, [theme])

    const handleClusterLayerClick = useCallback((event: any) => {
        const map = event.target
        const feature = event.feature
        const geometry = feature.geometry
        const clusterId = feature?.properties?.["cluster_id"]
        const source = map.getSource(CAMPAIGNS_SOURCE)
        source.getClusterExpansionZoom(clusterId, (error: any, zoom: any) => {
            if (error) {
                return
            }
            map.easeTo({
                center: geometry.coordinates,
                zoom: zoom + 1,
            })
        })
    }, [])

    const campaignClusterCountLayerConfig = useMemo(() => {
        if (!theme) {
            return null
        }
        return getClusterCountLayerConfig({
            layout: {
                "text-size": theme.typography["Desktop & Mobile/Heading/H2"].fontSize,
            },
            paint: {
                "text-color": theme.palette.map.text,
            },
        })
    }, [theme])

    return <MultiCampaignsSource
        id={CAMPAIGNS_SOURCE}
        cluster={true}
        clusterMaxZoom={MAX_ZOOM_LEVEL_CLUSTERED}
        hoveredCampaignId={hoveredCampaign?.uuid}
        hoveredChildren={
            <Layer
                {...getCampaignItemsLayerStyle(true)}
                id={CAMPAIGNS_LAYER_HOVERED}
                onClick={handleCampaignLayerClick}
            />
        }
    >
        <Layer
            {...getCampaignItemsLayerStyle()}
            id={CAMPAIGNS_LAYER}
            onClick={handleCampaignLayerClick}
        />
        <Layer
            {...campaignClusterLayerConfig}
            id={CAMPAIGNS_LAYER_CLUSTER}
            onClick={handleClusterLayerClick}
        />
        <Layer
            {...campaignClusterCountLayerConfig}
            id={CAMPAIGNS_LAYER_CLUSTER_COUNT}
        />
    </MultiCampaignsSource>
}
